import type {
  PartnerErrorCode,
  PartnerErrorField,
} from "@redotech/redo-partners-core/errors";

export interface FieldErrorMapping {
  /** Form field the error is attributable to, or null for form-level errors. */
  field: PartnerErrorField | null;
  /** User-visible message tailored to the admin form context. */
  message: string;
}

function assertNever(_x: never): never {
  throw new Error("Unexpected PartnerErrorCode");
}

/**
 * Convert a `PartnerError.code` from the server into a UI-ready
 * `{ field, message }` pair the create-partner modal can route to the
 * right form input.
 *
 * The switch is exhaustive over `PartnerErrorCode`. Adding a new error
 * subclass in `partners/core` without updating this mapper is a compile
 * error in the `default` branch.
 */
export function mapPartnerErrorCodeToFieldError(
  code: PartnerErrorCode,
): FieldErrorMapping {
  switch (code) {
    case "partner.email.already_exists":
      return {
        field: "email",
        message: "An account with this email already exists.",
      };

    case "partner.referral_code.invalid":
      return {
        field: "referralCode",
        message: "Referral code must be 3–20 characters, A–Z and 0–9 only.",
      };
    case "partner.referral_code.reserved":
      return {
        field: "referralCode",
        message: "This referral code is reserved.",
      };
    case "partner.referral_code.already_exists":
      return {
        field: "referralCode",
        message: "This referral code is already taken.",
      };
    case "partner.referral_code.generation_exhausted":
      return {
        field: "referralCode",
        message:
          "Could not auto-generate a unique code. Please supply one explicitly.",
      };

    case "partner.status.invalid_transition":
      return {
        field: "status",
        message:
          "This status change isn't allowed (terminated partners can't be reactivated).",
      };

    // Auth-side codes that don't map to a form field. These shouldn't
    // surface from the create-partner flow but the admin RPC layer reuses
    // the same code namespace for resend / detail flows; map them to
    // form-level errors so the same helper can serve those forms too.
    case "partner.auth.generic":
    case "partner.auth.magic_link_invalid":
    case "partner.auth.magic_link_already_used":
    case "partner.auth.partner_not_found":
    case "partner.auth.partner_terminated":
    case "partner.auth.partner_paused":
      return {
        field: null,
        message: "Something went wrong. Please try again.",
      };

    default:
      return assertNever(code);
  }
}

/**
 * Convenience wrapper: take any caught error from an admin RPC call and
 * pull out the field/message pair if it's a typed PartnerError. Returns
 * `null` for non-Partner errors so callers can fall through to a generic
 * form-level error message.
 */
export function mapPartnerErrorToFieldError(
  err: unknown,
): FieldErrorMapping | null {
  if (
    typeof err !== "object" ||
    err === null ||
    !("code" in err) ||
    typeof (err as { code: unknown }).code !== "string"
  ) {
    return null;
  }
  const code = (err as { code: string }).code;
  if (!isPartnerErrorCode(code)) return null;
  return mapPartnerErrorCodeToFieldError(code);
}

const PARTNER_ERROR_CODES: ReadonlySet<PartnerErrorCode> = new Set([
  "partner.auth.generic",
  "partner.auth.magic_link_invalid",
  "partner.auth.magic_link_already_used",
  "partner.auth.partner_not_found",
  "partner.auth.partner_terminated",
  "partner.auth.partner_paused",
  "partner.referral_code.invalid",
  "partner.referral_code.reserved",
  "partner.referral_code.already_exists",
  "partner.referral_code.generation_exhausted",
  "partner.email.already_exists",
  "partner.status.invalid_transition",
]);

function isPartnerErrorCode(code: string): code is PartnerErrorCode {
  return PARTNER_ERROR_CODES.has(code as PartnerErrorCode);
}
